/**
 * Convective hold detection from existing AWC hazard brief.
 *
 * Reads SIGMET / G-AIRMET already fetched by the desk's awcHazards path.
 * When convective signals (thunderstorm, convective SIGMET) are present
 * in the departure or enroute blocks, the trip gets a hold or warning
 * via the same constraint enforcement pattern as curfew / weight.
 *
 * Settings-driven: enabled flag, enforcement (flag vs block).
 */
import type { AwcHazardBrief, AwcHazard } from "./awcHazards";
import type { OpsConstraintEnforcement, OpsDeskSettings } from "./types";

const CONVECTIVE_RE =
  /\bTS\b|\bTSTM\b|\bTHUNDERSTORM\b|\bCONVECT(?:IVE)?\b|\bCB\b|\bTCU\b|\bSEV\s+TURB\b/i;

export type ConvectiveHoldVerdict = {
  hold: boolean;
  severity: "ok" | OpsConstraintEnforcement;
  hazards: AwcHazard[];
  message: string;
};

function isConvective(hazard: AwcHazard): boolean {
  return (
    CONVECTIVE_RE.test(hazard.hazard) ||
    CONVECTIVE_RE.test(hazard.headline) ||
    CONVECTIVE_RE.test(hazard.raw)
  );
}

/**
 * Evaluate convective hold from AWC hazard brief.
 *
 * Checks departure and enroute blocks for thunderstorm / convective signals.
 * Disabled when `convectiveHoldEnabled` is false (the default).
 */
export function evaluateConvectiveHold(input: {
  ops: Pick<OpsDeskSettings, "convectiveHoldEnabled" | "convectiveHoldEnforcement">;
  hazards?: AwcHazardBrief | null;
  originIcao?: string;
  destIcao?: string;
}): ConvectiveHoldVerdict {
  const ok: ConvectiveHoldVerdict = {
    hold: false,
    severity: "ok",
    hazards: [],
    message: "",
  };

  if (!input.ops.convectiveHoldEnabled) return ok;
  if (!input.hazards) return ok;

  const all = [
    ...input.hazards.sigmets,
    ...input.hazards.gairmets,
    ...input.hazards.pireps,
  ];

  const convective = all.filter(
    (h) =>
      isConvective(h) &&
      (h.block === "dep" || h.block === "enroute" || h.block === "dest"),
  );

  if (!convective.length) return ok;

  const from = (input.originIcao ?? "").toUpperCase();
  const to = (input.destIcao ?? "").toUpperCase();
  const pair = from && to ? `${from}–${to}` : from || to || "";
  const blocks = [...new Set(convective.map((h) => h.block))];
  const blockLabel = blocks.join(", ");
  const first = convective[0]!;
  const extra = convective.length > 1 ? ` (+${convective.length - 1} more)` : "";

  return {
    hold: true,
    severity: input.ops.convectiveHoldEnforcement === "block" ? "block" : "flag",
    hazards: convective,
    message: `CONVECTIVE HOLD  ${pair} — ${first.hazard}${extra} in ${blockLabel}.`,
  };
}
